/**
 * Gateway-backed usage hooks for the model home and the team dashboard.
 *
 * Every metric these return originates in the inference gateway: the D1
 * audit log for traffic, tokens, latency percentiles and cost, and the
 * two-tier probes for availability. The platform API is a typed pass-through
 * — it does no rate math and no re-aggregation.
 */

import { keepPreviousData } from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import {
  useGetDashboardOverviewApiDashboardOverviewGet,
  useGetTeamUptimeApiDashboardUptimeGet,
  useListTeamRequestsApiDashboardRequestsGet,
} from '@/api/generated/dashboard/dashboard'
import {
  useGetModelMetricsApiModelsModelIdMetricsGet,
  useGetModelUptimeApiModelsModelIdUptimeGet,
  useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet,
} from '@/api/generated/models/models'
import type {
  DashboardOverviewResponse,
  ModelMetricsResponse,
  RequestLogResponse,
  UptimeResponse,
  UsageTimeseriesResponse,
} from '@/api/generated/model'
import type { ListTeamRequestsApiDashboardRequestsGetRange } from '@/api/generated/model/listTeamRequestsApiDashboardRequestsGetRange'
import type { ListTeamRequestsApiDashboardRequestsGetParams } from '@/api/generated/model/listTeamRequestsApiDashboardRequestsGetParams'
import type { GetModelMetricsApiModelsModelIdMetricsGetParams } from '@/api/generated/model/getModelMetricsApiModelsModelIdMetricsGetParams'
import type { RequestStatusFilter } from '@/components/models/home/requestLogOptions'

/** The window toggle on the requests band. */
export type UsageWindow = 'today' | 'week' | 'month'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  isError: boolean
  error: ApiRequestError | null
}

export function useModelUsageTimeseries(
  modelId: string | undefined,
  window: UsageWindow,
): UnwrappedQuery<UsageTimeseriesResponse> {
  const query = useGetModelUsageTimeseriesApiModelsModelIdUsageTimeseriesGet(
    modelId ?? '',
    { range: window },
    { query: { enabled: !!modelId } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

/**
 * The metrics read's query. At now `end` is left out: the generated client
 * writes a null param as the text "null", which the api refuses with a 422
 * (the page showed "Failed to load gateway metrics." at now after #1350).
 */
export function metricsParams(
  hours: number,
  end: number | null,
): GetModelMetricsApiModelsModelIdMetricsGetParams {
  return end === null ? { hours } : { hours, end: new Date(end).toISOString() }
}

/** A read whose data on screen can be the previous window's while the next one loads. */
export interface KeptQuery<TData> extends UnwrappedQuery<TData> {
  /** `data` is the previous window's, kept while this one loads. */
  isPlaceholderData: boolean
  isFetching: boolean
}

/**
 * The metrics page's one read (TRA-1461): a window of `hours` bucketed by
 * its length, with the status split, both percentile sets, tokens and the
 * top error messages. `end` is where the window ends once the page moved
 * it back (TRA-1592), null at now. A stretch in the past does not change,
 * so only the window at now refetches every minute. A move to another
 * window keeps the previous one on screen until the new one lands, so the
 * cards never go blank on an arrow press.
 */
export function useModelMetrics(
  modelId: string | undefined,
  hours: number,
  end: number | null,
): KeptQuery<ModelMetricsResponse> {
  const query = useGetModelMetricsApiModelsModelIdMetricsGet(
    modelId ?? '',
    metricsParams(hours, end),
    {
      query: {
        enabled: !!modelId,
        refetchInterval: end === null ? 60_000 : false,
        placeholderData: keepPreviousData,
      },
    },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isPlaceholderData: query.isPlaceholderData,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

/**
 * The viewer's zone offset, JS convention (UTC − local, minutes). The uptime
 * wall's 8h blocks are bucketed server-side in this offset (TRA-918/919) so
 * its rows and outage spans agree with the locally-rendered Requests chart.
 * Read per call, not at module load — a laptop that crosses a DST boundary
 * mid-session refetches with the current offset.
 */
function tzOffsetMinutes(): number {
  return new Date().getTimezoneOffset()
}

export function useModelUptime(
  modelId: string | undefined,
): UnwrappedQuery<UptimeResponse> {
  const query = useGetModelUptimeApiModelsModelIdUptimeGet(
    modelId ?? '',
    { tz_offset_minutes: tzOffsetMinutes() },
    { query: { enabled: !!modelId } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

export function useDashboardOverview(
  window: UsageWindow,
): UnwrappedQuery<DashboardOverviewResponse> {
  const query = useGetDashboardOverviewApiDashboardOverviewGet({ range: window })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

export type TeamRequestRange = ListTeamRequestsApiDashboardRequestsGetRange
export type { RequestStatusFilter }

interface UseTeamRequestsArgs {
  /** Search, filters, sort and paging as the endpoint takes them (TRA-1397). */
  params: ListTeamRequestsApiDashboardRequestsGetParams
  /** The shared log component calls this AND the per-model hook (hooks can't
      be conditional); this gate keeps the unused one off the network. */
  enabled?: boolean
}

/**
 * The team's whole request log — every model the team called, including
 * public catalog slugs that have no platform Model row. The per-model log
 * can only show traffic to registered slugs, which is exactly the traffic
 * you are NOT looking for when the dashboard total doesn't add up.
 */
export function useTeamRequests({
  params,
  enabled = true,
}: UseTeamRequestsArgs): UnwrappedQuery<RequestLogResponse> {
  const query = useListTeamRequestsApiDashboardRequestsGet(params, { query: { enabled } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}

/**
 * Team-wide availability, fetched separately from the overview because it
 * does not depend on the requests window — folding it in would refetch
 * ninety days of blocks on every Today/7-day/30-day toggle.
 */
export function useTeamUptime(): UnwrappedQuery<UptimeResponse> {
  const query = useGetTeamUptimeApiDashboardUptimeGet({
    tz_offset_minutes: tzOffsetMinutes(),
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  }
}
